/**
 * Generates a 1500x1500 JPEG cover artwork from a release title and artist name.
 *
 * Uses the same gradient palette aesthetic as the existing CoverTemplateEditor
 * "gradient" template, so auto-covers feel consistent with manually-edited ones.
 * Picks a deterministic palette based on the title hash to keep the same release
 * looking the same on every regeneration.
 *
 * Output is meant to be uploaded via `uploadService.uploadImage` to `distro-covers`.
 *
 * @param {Object} options
 * @param {string} options.title  Track / release title
 * @param {string} options.artist Artist name
 * @returns {Promise<File>} JPEG File ready to upload
 */

const PALETTES = [
  { name: 'Sunset', primary: '#f6ad55', accent: '#e53e3e', text: '#ffffff' },
  { name: 'Ocean', primary: '#2b6cb0', accent: '#38b2ac', text: '#ffffff' },
  { name: 'Twilight', primary: '#553c9a', accent: '#d53f8c', text: '#ffffff' },
  { name: 'Forest', primary: '#276749', accent: '#68d391', text: '#ffffff' },
  { name: 'Volcano', primary: '#742a2a', accent: '#dd6b20', text: '#ffffff' },
  { name: 'Aurora', primary: '#3182ce', accent: '#805ad5', text: '#ffffff' },
  { name: 'Royal', primary: '#1a365d', accent: '#7189ff', text: '#ffffff' },
  { name: 'Berry', primary: '#702459', accent: '#cb9cf2', text: '#ffffff' },
  { name: 'Ember', primary: '#171923', accent: '#f96900', text: '#ffffff' },
  { name: 'Indie', primary: '#2e294e', accent: '#17bebb', text: '#ffffff' },
];

const hashString = (input) => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) - hash) + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(/\s+/);
  const lines = [];
  let current = '';

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
};

export async function generateAutoCover({ title = 'Untitled', artist = 'Unknown' } = {}) {
  const size = 1500;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  const palette = PALETTES[hashString(`${title}|${artist}`) % PALETTES.length];

  const gradient = ctx.createLinearGradient(0, 0, size, size);
  gradient.addColorStop(0, palette.primary);
  gradient.addColorStop(1, palette.accent);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);

  ctx.save();
  ctx.globalAlpha = 0.12;
  ctx.fillStyle = '#ffffff';
  for (let i = 0; i < 6; i++) {
    const radius = 200 + (hashString(`${title}|circle-${i}`) % 350);
    const x = (hashString(`${title}|x-${i}`) % size);
    const y = (hashString(`${artist}|y-${i}`) % size);
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  ctx.fillStyle = palette.text;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  const margin = 110;
  const maxTextWidth = size - margin * 2;

  ctx.font = '700 130px "Montserrat", "Helvetica Neue", Arial, sans-serif';
  const titleLines = wrapText(ctx, title, maxTextWidth).slice(0, 3);
  const titleLineHeight = 150;

  ctx.font = '500 60px "Inter", "Helvetica Neue", Arial, sans-serif';
  const artistText = artist.toUpperCase();
  const artistMetrics = ctx.measureText(artistText);
  const artistY = size - margin - 80;
  const titleStartY = artistY - (titleLines.length * titleLineHeight) - 60;

  ctx.font = '700 130px "Montserrat", "Helvetica Neue", Arial, sans-serif';
  titleLines.forEach((line, i) => {
    ctx.fillText(line, margin, titleStartY + i * titleLineHeight);
  });

  ctx.font = '500 60px "Inter", "Helvetica Neue", Arial, sans-serif';
  ctx.fillText(artistText, margin, artistY);

  ctx.strokeStyle = palette.text;
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(margin, artistY - 30);
  ctx.lineTo(margin + Math.min(artistMetrics.width, 500), artistY - 30);
  ctx.stroke();

  const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
  const blob = await (await fetch(dataUrl)).blob();
  const safeTitle = title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'cover';
  return new File([blob], `${safeTitle}-cover.jpg`, { type: 'image/jpeg' });
}

export default generateAutoCover;
